import React, { useEffect, useState } from 'react';
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Divider,
} from '@mui/material';
import {
  Warning as WarningIcon,
  Error as ErrorIcon,
  CheckCircle as CheckCircleIcon,
  Today as TodayIcon,
  CalendarViewWeek as WeekIcon,
  CalendarMonth as MonthIcon,
  People as PeopleIcon,
  Work as WorkIcon,
  Cancel as CancelIcon,
  WarningAmber as WarningAmberIcon,
  EventBusy as EventBusyIcon,
  BeachAccess as BeachAccessIcon,
  AccessTime as AccessTimeIcon,
  Notifications as NotificationsIcon,
  Check as CheckIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from '../hooks/usePermissions';
import { anomaliesAPI, leaveAdminAPI, checkoutAdminAPI } from '../services/api';

interface DashboardStats {
  total_dipendenti: number;
  al_lavoro: number;
  contratto_cessato: number;
  contratto_in_scadenza: number;
  in_permesso: number;
  in_ferie: number;
}

interface AnomalySummary {
  total: number;
  severe: number;
  moderate: number;
  minor: number;
  employees: AnomalyEmployee[];
}

interface AnomalyEmployee {
  employee_id: number;
  nome: string;
  cognome: string;
  codice_fiscale: string;
  dipartimento: string;
  ore_previste: number;
  ore_effettive: number;
  differenza: number;
  percentuale_completamento: number;
  gravita: 'severe' | 'moderate' | 'minor';
  periodo: string;
}

interface LeaveRequest {
  id: number;
  employee_id: number;
  employee_name: string;
  employee_surname: string;
  request_type: 'ferie' | 'permesso' | 'malattia' | 'altri';
  start_date: string;
  end_date?: string;
  duration_hours?: number;
  duration_days?: number;
  status: 'pending' | 'approved' | 'rejected';
  notes?: string;
  request_date: string;
}

const Dashboard: React.FC = () => {
  const { user } = useAuth();
  const { isAdmin } = usePermissions();
  const [loading, setLoading] = useState(true);
  const [dashboardStats, setDashboardStats] = useState<DashboardStats | null>(null);
  const [anomaliesDaily, setAnomaliesDaily] = useState<AnomalySummary | null>(null);
  const [anomaliesWeekly, setAnomaliesWeekly] = useState<AnomalySummary | null>(null);
  const [anomaliesMonthly, setAnomaliesMonthly] = useState<AnomalySummary | null>(null);
  const [selectedAnomalies, setSelectedAnomalies] = useState<AnomalyEmployee[]>([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogTitle, setDialogTitle] = useState('');
  const [pendingLeaveRequests, setPendingLeaveRequests] = useState<LeaveRequest[]>([]);
  const [leaveDialogOpen, setLeaveDialogOpen] = useState(false);
  const [pendingCheckouts, setPendingCheckouts] = useState<any[]>([]);
  const [checkoutDialogOpen, setCheckoutDialogOpen] = useState(false);
  const [employeesListDialogOpen, setEmployeesListDialogOpen] = useState(false);
  const [employeesList, setEmployeesList] = useState<any[]>([]);
  const [employeesListTitle, setEmployeesListTitle] = useState('');
  const [loadingEmployeesList, setLoadingEmployeesList] = useState(false);

  useEffect(() => {
    if (!isAdmin) {
      setLoading(false);
      return;
    }

    const defaultStats = { total_dipendenti: 0, al_lavoro: 0, contratto_cessato: 0, contratto_in_scadenza: 0, in_permesso: 0, in_ferie: 0 };
    const defaultAnomalies: AnomalySummary = { total: 0, severe: 0, moderate: 0, minor: 0, employees: [] };

    // Chiamate indipendenti: la pagina mostra subito le card e ogni dato compare
    // appena pronto, senza attendere la chiamata piu lenta. Le anomalie
    // settimanali/mensili possono richiedere qualche secondo e non bloccano piu
    // il resto della dashboard.
    const loadDashboard = () => {
      anomaliesAPI.getDashboardStats().then(setDashboardStats).catch(() => setDashboardStats(defaultStats));
      leaveAdminAPI.getPendingRequests().then((r) => setPendingLeaveRequests(r.requests || [])).catch(() => setPendingLeaveRequests([]));
      checkoutAdminAPI.getPendingCheckouts().then((r) => setPendingCheckouts(r.pending_checkouts || [])).catch(() => setPendingCheckouts([]));
      anomaliesAPI.getDailyAnomalies().then(setAnomaliesDaily).catch(() => setAnomaliesDaily(defaultAnomalies));
      anomaliesAPI.getWeeklyAnomalies().then(setAnomaliesWeekly).catch(() => setAnomaliesWeekly(defaultAnomalies));
      anomaliesAPI.getMonthlyAnomalies().then(setAnomaliesMonthly).catch(() => setAnomaliesMonthly(defaultAnomalies));
    };

    setLoading(false);
    loadDashboard();

    // Refresh ogni 5 minuti
    const interval = setInterval(loadDashboard, 300000);
    return () => clearInterval(interval);
  }, [user?.role]);

  const handleCardClick = (anomalies: AnomalyEmployee[], period: string) => {
    setSelectedAnomalies(anomalies);
    setDialogTitle(`Dipendenti in Anomalia - ${period}`);
    setDialogOpen(true);
  };

  const handleLeaveRequestsClick = () => {
    setLeaveDialogOpen(true);
  };

  const handlePendingCheckoutsClick = () => {
    setCheckoutDialogOpen(true);
  };

  const handleStatCardClick = async (category: string) => {
    setLoadingEmployeesList(true);
    setEmployeesListDialogOpen(true);
    
    try {
      let employees: any[] = [];
      let title = '';
      
      switch (category) {
        case 'al-lavoro':
          title = 'Dipendenti al Lavoro';
          const alLavoroData = await anomaliesAPI.getEmployeesAlLavoro();
          employees = alLavoroData?.employees || alLavoroData || [];
          break;
        case 'contratto-cessato':
          title = 'Dipendenti con Contratto Cessato';
          const cessatiData = await anomaliesAPI.getEmployeesContrattoCessato();
          employees = cessatiData.employees || [];
          break;
        case 'contratto-in-scadenza':
          title = 'Dipendenti con Contratto in Scadenza';
          const scadenzaData = await anomaliesAPI.getEmployeesContrattoInScadenza();
          employees = scadenzaData.employees || [];
          break;
        case 'in-permesso':
          title = 'Dipendenti in Permesso';
          const permessoData = await anomaliesAPI.getEmployeesInPermesso();
          employees = permessoData.employees || [];
          break;
        case 'in-ferie':
          title = 'Dipendenti in Ferie';
          const ferieData = await anomaliesAPI.getEmployeesInFerie();
          employees = ferieData.employees || [];
          break;
        default:
          setLoadingEmployeesList(false);
          return;
      }
      
      setEmployeesListTitle(title);
      setEmployeesList(employees);
    } catch (error) {
      alert('Errore nel caricamento dell\'elenco. Riprova.');
    } finally {
      setLoadingEmployeesList(false);
    }
  };

  const handleForceCheckout = async (presenceId: number) => {
    try {
      await checkoutAdminAPI.forceCheckout(presenceId);
      // Ricarica i dati
      const checkouts = await checkoutAdminAPI.getPendingCheckouts();
      setPendingCheckouts(checkouts.pending_checkouts || []);
      // Ricarica anche le statistiche
      const stats = await anomaliesAPI.getDashboardStats();
      setDashboardStats(stats);
    } catch (error) {
      alert('Errore nel checkout forzato. Riprova.');
    }
  };

  const handleAutoCheckout = async () => {
    if (!confirm('Vuoi eseguire il checkout automatico per tutte le presenze con più di 12 ore?')) {
      return;
    }
    try {
      const result = await checkoutAdminAPI.autoCheckoutExpired(12);
      alert(`Checkout automatico completato per ${result.count} presenze`);
      // Ricarica i dati
      const checkouts = await checkoutAdminAPI.getPendingCheckouts();
      setPendingCheckouts(checkouts.pending_checkouts || []);
      const stats = await anomaliesAPI.getDashboardStats();
      setDashboardStats(stats);
    } catch (error) {
      alert('Errore nel checkout automatico. Riprova.');
    }
  };

  const handleApproveRequest = async (requestId: number) => {
    try {
      await leaveAdminAPI.approveRequest(requestId, { 
        status: 'approved',
        approver_notes: ''
      });
      // Ricarica i dati
      const leaveRequests = await leaveAdminAPI.getPendingRequests();
      setPendingLeaveRequests(leaveRequests.requests || []);
    } catch (error) {
      // Errore nell'approvazione della richiesta
    }
  };

  const handleRejectRequest = async (requestId: number) => {
    try {
      await leaveAdminAPI.approveRequest(requestId, { 
        status: 'rejected',
        approver_notes: ''
      });
      // Ricarica i dati
      const leaveRequests = await leaveAdminAPI.getPendingRequests();
      setPendingLeaveRequests(leaveRequests.requests || []);
    } catch (error) {
      // Errore nel rifiuto della richiesta
    }
  };

  const getRequestTypeLabel = (type: string) => {
    switch (type) {
      case 'ferie':
        return 'Ferie';
      case 'permesso':
        return 'Permesso';
      case 'malattia':
        return 'Malattia';
      case 'altri':
        return 'Altri';
      default:
        return type;
    }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'severe':
        return 'error';
      case 'moderate':
        return 'warning';
      case 'minor':
        return 'info';
      default:
        return 'default';
    }
  };

  const getSeverityLabel = (severity: string) => {
    switch (severity) {
      case 'severe':
        return 'Critica';
      case 'moderate':
        return 'Media';
      case 'minor':
        return 'Lieve';
      default:
        return 'N/A';
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  // Dashboard per dipendenti (solo se non è admin)
  if (!isAdmin) {
    return (
      <Box>
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold', color: '#0d47a1' }}>
          Benvenuto, {user?.nome} {user?.cognome}
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
          Accedi alla sezione "Presenze" per registrare le tue ore di lavoro.
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* Titolo Dashboard */}
      <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold', color: '#0d47a1', mb: 1 }}>
        Dashboard Amministrativa Centrale
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
        Vista complessiva trasversale su dipendenti, anomalie e attivita da presidiare.
      </Typography>

      {/* SEZIONE 1: STATISTICHE GENERALI */}
      <Box mb={4}>
        <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', mb: 2 }}>
          Statistiche Generali
        </Typography>
        <Grid container spacing={3}>
          {/* Totale Dipendenti */}
          <Grid item xs={12} sm={6} md={4} lg={2}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" mb={1}>
                  <PeopleIcon sx={{ mr: 1, color: '#0d47a1' }} />
                  <Typography variant="body2" color="text.secondary">
                    Totale Dipendenti
                  </Typography>
                </Box>
                <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#0d47a1' }}>
                  {dashboardStats?.total_dipendenti || 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          {/* Al Lavoro */}
          <Grid item xs={12} sm={6} md={4} lg={2}>
            <Card 
              sx={{ 
                borderLeft: '4px solid #4caf50',
                cursor: (dashboardStats?.al_lavoro || 0) > 0 ? 'pointer' : 'default',
                '&:hover': (dashboardStats?.al_lavoro || 0) > 0 ? { 
                  boxShadow: 6,
                  transform: 'translateY(-4px)',
                  transition: 'all 0.3s'
                } : {}
              }}
              onClick={() => (dashboardStats?.al_lavoro || 0) > 0 && handleStatCardClick('al-lavoro')}
            >
              <CardContent>
                <Box display="flex" alignItems="center" mb={1}>
                  <WorkIcon sx={{ mr: 1, color: '#4caf50' }} />
                  <Typography variant="body2" color="text.secondary">
                    Al Lavoro
                  </Typography>
                </Box>
                <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#4caf50' }}>
                  {dashboardStats?.al_lavoro || 0}
                </Typography>
                {(dashboardStats?.al_lavoro || 0) > 0 && (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                    Clicca per dettagli
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Contratti Cessati */}
          <Grid item xs={12} sm={6} md={4} lg={2}>
            <Card 
              sx={{ 
                borderLeft: '4px solid #f44336',
                cursor: (dashboardStats?.contratto_cessato || 0) > 0 ? 'pointer' : 'default',
                '&:hover': (dashboardStats?.contratto_cessato || 0) > 0 ? { 
                  boxShadow: 6,
                  transform: 'translateY(-4px)',
                  transition: 'all 0.3s'
                } : {}
              }}
              onClick={() => (dashboardStats?.contratto_cessato || 0) > 0 && handleStatCardClick('contratto-cessato')}
            >
              <CardContent>
                <Box display="flex" alignItems="center" mb={1}>
                  <CancelIcon sx={{ mr: 1, color: '#f44336' }} />
                  <Typography variant="body2" color="text.secondary">
                    Contratti Cessati
                  </Typography>
                </Box>
                <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#f44336' }}>
                  {dashboardStats?.contratto_cessato || 0}
                </Typography>
                {(dashboardStats?.contratto_cessato || 0) > 0 && (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                    Clicca per dettagli
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Contratti in Scadenza */}
          <Grid item xs={12} sm={6} md={4} lg={2}>
            <Card 
              sx={{ 
                borderLeft: '4px solid #ff9800',
                cursor: (dashboardStats?.contratto_in_scadenza || 0) > 0 ? 'pointer' : 'default',
                '&:hover': (dashboardStats?.contratto_in_scadenza || 0) > 0 ? { 
                  boxShadow: 6,
                  transform: 'translateY(-4px)',
                  transition: 'all 0.3s'
                } : {}
              }}
              onClick={() => (dashboardStats?.contratto_in_scadenza || 0) > 0 && handleStatCardClick('contratto-in-scadenza')}
            >
              <CardContent>
                <Box display="flex" alignItems="center" mb={1}>
                  <WarningAmberIcon sx={{ mr: 1, color: '#ff9800' }} />
                  <Typography variant="body2" color="text.secondary">
                    In Scadenza
                  </Typography>
                </Box>
                <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#ff9800' }}>
                  {dashboardStats?.contratto_in_scadenza || 0}
                </Typography>
                {(dashboardStats?.contratto_in_scadenza || 0) > 0 && (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                    Clicca per dettagli
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* In Permesso */}
          <Grid item xs={12} sm={6} md={4} lg={2}>
            <Card 
              sx={{ 
                borderLeft: '4px solid #2196f3',
                cursor: (dashboardStats?.in_permesso || 0) > 0 ? 'pointer' : 'default',
                '&:hover': (dashboardStats?.in_permesso || 0) > 0 ? { 
                  boxShadow: 6,
                  transform: 'translateY(-4px)',
                  transition: 'all 0.3s'
                } : {}
              }}
              onClick={() => (dashboardStats?.in_permesso || 0) > 0 && handleStatCardClick('in-permesso')}
            >
              <CardContent>
                <Box display="flex" alignItems="center" mb={1}>
                  <EventBusyIcon sx={{ mr: 1, color: '#2196f3' }} />
                  <Typography variant="body2" color="text.secondary">
                    In Permesso
                  </Typography>
                </Box>
                <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#2196f3' }}>
                  {dashboardStats?.in_permesso || 0}
                </Typography>
                {(dashboardStats?.in_permesso || 0) > 0 && (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                    Clicca per dettagli
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>

                     {/* In Ferie */}
           <Grid item xs={12} sm={6} md={4} lg={2}>
             <Card 
               sx={{ 
                 borderLeft: '4px solid #00bcd4',
                 cursor: (dashboardStats?.in_ferie || 0) > 0 ? 'pointer' : 'default',
                 '&:hover': (dashboardStats?.in_ferie || 0) > 0 ? { 
                   boxShadow: 6,
                   transform: 'translateY(-4px)',
                   transition: 'all 0.3s'
                 } : {}
               }}
               onClick={() => (dashboardStats?.in_ferie || 0) > 0 && handleStatCardClick('in-ferie')}
             >
               <CardContent>
                 <Box display="flex" alignItems="center" mb={1}>
                   <BeachAccessIcon sx={{ mr: 1, color: '#00bcd4' }} />
                   <Typography variant="body2" color="text.secondary">
                     In Ferie
                   </Typography>
                 </Box>
                 <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#00bcd4' }}>
                   {dashboardStats?.in_ferie || 0}
                 </Typography>
                 {(dashboardStats?.in_ferie || 0) > 0 && (
                   <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                     Clicca per dettagli
                   </Typography>
                 )}
               </CardContent>
             </Card>
           </Grid>

          {/* Checkout Pendenti */}
          <Grid item xs={12} sm={6} md={4} lg={2}>
            <Card 
              sx={{ 
                borderLeft: '4px solid #ff5722',
                cursor: pendingCheckouts.length > 0 ? 'pointer' : 'default',
                '&:hover': pendingCheckouts.length > 0 ? { 
                  boxShadow: 6,
                  transform: 'translateY(-4px)',
                  transition: 'all 0.3s'
                } : {}
              }}
              onClick={pendingCheckouts.length > 0 ? handlePendingCheckoutsClick : undefined}
            >
              <CardContent>
                <Box display="flex" alignItems="center" mb={1}>
                  <AccessTimeIcon sx={{ mr: 1, color: '#ff5722' }} />
                  <Typography variant="body2" color="text.secondary">
                    Checkout Pendenti
                  </Typography>
                </Box>
                <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#ff5722' }}>
                  {pendingCheckouts.length}
                </Typography>
                {pendingCheckouts.length > 0 && (
                  <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
                    Clicca per gestire
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>

           {/* Richieste Ferie/Permessi in Attesa */}
           <Grid item xs={12}>
             <Card 
               sx={{ 
                 borderLeft: '4px solid #9c27b0',
                 cursor: pendingLeaveRequests.length > 0 ? 'pointer' : 'default',
                 '&:hover': pendingLeaveRequests.length > 0 ? { 
                   boxShadow: 6,
                   transform: 'translateY(-4px)',
                   transition: 'all 0.3s'
                 } : {}
               }}
               onClick={handleLeaveRequestsClick}
             >
               <CardContent>
                 <Box display="flex" alignItems="center" mb={1}>
                   <NotificationsIcon sx={{ mr: 1, color: '#9c27b0' }} />
                   <Typography variant="body2" color="text.secondary">
                     Richieste Ferie/Permessi in Attesa
                   </Typography>
                 </Box>
                 <Box display="flex" alignItems="baseline" gap={1}>
                   <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#9c27b0' }}>
                     {pendingLeaveRequests.length}
                   </Typography>
                   <Typography variant="body2" color="text.secondary">
                     {pendingLeaveRequests.length === 1 ? 'richiesta' : 'richieste'}
                   </Typography>
                 </Box>
               </CardContent>
             </Card>
           </Grid>
         </Grid>
       </Box>

      <Divider sx={{ my: 4 }} />

      {/* SEZIONE 2: MONITORAGGIO ANOMALIE */}
      <Box>
        <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', mb: 2 }}>
          Anomalie e Controlli
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Discrepanze tra ore previste e ore effettive lavorate
        </Typography>

        <Grid container spacing={3}>
          {/* Card Anomalie Giornaliere */}
          <Grid item xs={12} md={4}>
            <Card 
              sx={{ 
                cursor: anomaliesDaily?.total ? 'pointer' : 'default',
                '&:hover': anomaliesDaily?.total ? { 
                  boxShadow: 6,
                  transform: 'translateY(-4px)',
                  transition: 'all 0.3s'
                } : {}
              }}
              onClick={() => anomaliesDaily && handleCardClick(anomaliesDaily.employees, 'Giornaliero')}
            >
              <CardContent>
                <Box display="flex" alignItems="center" mb={2}>
                  <TodayIcon color="primary" sx={{ mr: 1, fontSize: 32 }} />
                  <Box>
                    <Typography variant="h6" color="text.secondary">
                      Anomalie Giornaliere
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Oggi
                    </Typography>
                  </Box>
                </Box>
                
                <Box display="flex" alignItems="baseline" gap={1}>
                  <Typography variant="h3" sx={{ fontWeight: 'bold', color: anomaliesDaily?.total ? '#f44336' : '#4caf50' }}>
                    {anomaliesDaily === null ? <CircularProgress size={28} /> : (anomaliesDaily.total || 0)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    dipendenti
                  </Typography>
                </Box>

                {anomaliesDaily && anomaliesDaily.total > 0 && (
                  <Box mt={2} display="flex" gap={1} flexWrap="wrap">
                    {anomaliesDaily.severe > 0 && (
                      <Chip 
                        label={`${anomaliesDaily.severe} Critiche`} 
                        color="error" 
                        size="small" 
                        icon={<ErrorIcon />}
                      />
                    )}
                    {anomaliesDaily.moderate > 0 && (
                      <Chip 
                        label={`${anomaliesDaily.moderate} Medie`} 
                        color="warning" 
                        size="small" 
                        icon={<WarningIcon />}
                      />
                    )}
                    {anomaliesDaily.minor > 0 && (
                      <Chip 
                        label={`${anomaliesDaily.minor} Lievi`} 
                        color="info" 
                        size="small" 
                      />
                    )}
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Card Anomalie Settimanali */}
          <Grid item xs={12} md={4}>
            <Card 
              sx={{ 
                cursor: anomaliesWeekly?.total ? 'pointer' : 'default',
                '&:hover': anomaliesWeekly?.total ? { 
                  boxShadow: 6,
                  transform: 'translateY(-4px)',
                  transition: 'all 0.3s'
                } : {}
              }}
              onClick={() => anomaliesWeekly && handleCardClick(anomaliesWeekly.employees, 'Settimanale')}
            >
              <CardContent>
                <Box display="flex" alignItems="center" mb={2}>
                  <WeekIcon color="primary" sx={{ mr: 1, fontSize: 32 }} />
                  <Box>
                    <Typography variant="h6" color="text.secondary">
                      Anomalie Settimanali
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Questa Settimana
                    </Typography>
                  </Box>
                </Box>
                
                <Box display="flex" alignItems="baseline" gap={1}>
                  <Typography variant="h3" sx={{ fontWeight: 'bold', color: anomaliesWeekly?.total ? '#f44336' : '#4caf50' }}>
                    {anomaliesWeekly === null ? <CircularProgress size={28} /> : (anomaliesWeekly.total || 0)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    dipendenti
                  </Typography>
                </Box>

                {anomaliesWeekly && anomaliesWeekly.total > 0 && (
                  <Box mt={2} display="flex" gap={1} flexWrap="wrap">
                    {anomaliesWeekly.severe > 0 && (
                      <Chip 
                        label={`${anomaliesWeekly.severe} Critiche`} 
                        color="error" 
                        size="small" 
                        icon={<ErrorIcon />}
                      />
                    )}
                    {anomaliesWeekly.moderate > 0 && (
                      <Chip 
                        label={`${anomaliesWeekly.moderate} Medie`} 
                        color="warning" 
                        size="small" 
                        icon={<WarningIcon />}
                      />
                    )}
                    {anomaliesWeekly.minor > 0 && (
                      <Chip 
                        label={`${anomaliesWeekly.minor} Lievi`} 
                        color="info" 
                        size="small" 
                      />
                    )}
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Card Anomalie Mensili */}
          <Grid item xs={12} md={4}>
            <Card 
              sx={{ 
                cursor: anomaliesMonthly?.total ? 'pointer' : 'default',
                '&:hover': anomaliesMonthly?.total ? { 
                  boxShadow: 6,
                  transform: 'translateY(-4px)',
                  transition: 'all 0.3s'
                } : {}
              }}
              onClick={() => anomaliesMonthly && handleCardClick(anomaliesMonthly.employees, 'Mensile')}
            >
              <CardContent>
                <Box display="flex" alignItems="center" mb={2}>
                  <MonthIcon color="primary" sx={{ mr: 1, fontSize: 32 }} />
                  <Box>
                    <Typography variant="h6" color="text.secondary">
                      Anomalie Mensili
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Questo Mese
                    </Typography>
                  </Box>
                </Box>
                
                <Box display="flex" alignItems="baseline" gap={1}>
                  <Typography variant="h3" sx={{ fontWeight: 'bold', color: anomaliesMonthly?.total ? '#f44336' : '#4caf50' }}>
                    {anomaliesMonthly === null ? <CircularProgress size={28} /> : (anomaliesMonthly.total || 0)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    dipendenti
                  </Typography>
                </Box>

                {anomaliesMonthly && anomaliesMonthly.total > 0 && (
                  <Box mt={2} display="flex" gap={1} flexWrap="wrap">
                    {anomaliesMonthly.severe > 0 && (
                      <Chip 
                        label={`${anomaliesMonthly.severe} Critiche`} 
                        color="error" 
                        size="small" 
                        icon={<ErrorIcon />}
                      />
                    )}
                    {anomaliesMonthly.moderate > 0 && (
                      <Chip 
                        label={`${anomaliesMonthly.moderate} Medie`} 
                        color="warning" 
                        size="small" 
                        icon={<WarningIcon />}
                      />
                    )}
                    {anomaliesMonthly.minor > 0 && (
                      <Chip 
                        label={`${anomaliesMonthly.minor} Lievi`} 
                        color="info" 
                        size="small" 
                      />
                    )}
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Riepilogo Status */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" mb={2}>
                  <CheckCircleIcon color="success" sx={{ mr: 1 }} />
                  <Typography variant="h6">
                    Stato Monitoraggio
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  {anomaliesDaily?.total === 0 && anomaliesWeekly?.total === 0 && anomaliesMonthly?.total === 0
                    ? 'Nessuna anomalia rilevata. Tutti i dipendenti rispettano gli orari previsti.'
                    : '⚠️ Attenzione: sono state rilevate anomalie. Clicca sulle card sopra per visualizzare i dettagli.'}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>

             {/* Dialog per visualizzare richieste ferie/permessi */}
       <Dialog open={leaveDialogOpen} onClose={() => setLeaveDialogOpen(false)} maxWidth="md" fullWidth>
         <DialogTitle>
           Richieste Ferie/Permessi in Attesa
           <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>
             Totale: {pendingLeaveRequests.length} richieste
           </Typography>
         </DialogTitle>
         
         <DialogContent>
           {pendingLeaveRequests.length === 0 ? (
             <Box textAlign="center" py={4}>
               <CheckCircleIcon sx={{ fontSize: 48, color: '#4caf50', mb: 2 }} />
               <Typography variant="h6" color="text.secondary">
                 Nessuna richiesta in attesa
               </Typography>
             </Box>
           ) : (
             <Box>
               {pendingLeaveRequests.map((request) => (
                 <Card key={request.id} sx={{ mb: 2 }}>
                   <CardContent>
                     <Box display="flex" justifyContent="space-between" alignItems="start" mb={2}>
                       <Box>
                         <Typography variant="h6">
                           {request.employee_name} {request.employee_surname}
                         </Typography>
                         <Typography variant="body2" color="text.secondary">
                           Tipo: {getRequestTypeLabel(request.request_type)}
                         </Typography>
                       </Box>
                       <Chip 
                         label="In Attesa" 
                         color="warning" 
                         size="small"
                       />
                     </Box>
                     
                     <Box mb={2}>
                       <Typography variant="body2">
                         <strong>Periodo:</strong> {new Date(request.start_date).toLocaleDateString('it-IT')}
                         {request.end_date && ` - ${new Date(request.end_date).toLocaleDateString('it-IT')}`}
                       </Typography>
                       {request.duration_days && (
                         <Typography variant="body2">
                           <strong>Durata:</strong> {request.duration_days} giorno{request.duration_days !== 1 ? 'i' : ''}
                         </Typography>
                       )}
                       {request.duration_hours && (
                         <Typography variant="body2">
                           <strong>Ore:</strong> {request.duration_hours}h
                         </Typography>
                       )}
                                               {request.notes && (
                          <Typography variant="body2" sx={{ mt: 1 }}>
                            <strong>Note:</strong> {request.notes}
                          </Typography>
                        )}
                      </Box>
                      
                      <Box display="flex" gap={2} mt={2}>
                        <Button
                          variant="contained"
                          color="success"
                          startIcon={<CheckIcon />}
                          onClick={() => handleApproveRequest(request.id)}
                          fullWidth
                        >
                          Approva
                        </Button>
                        <Button
                          variant="outlined"
                          color="error"
                          startIcon={<CloseIcon />}
                          onClick={() => handleRejectRequest(request.id)}
                          fullWidth
                        >
                          Rifiuta
                        </Button>
                      </Box>
                    </CardContent>
                  </Card>
                ))}
             </Box>
           )}
         </DialogContent>
         
         <DialogActions>
           <Button onClick={() => setLeaveDialogOpen(false)} variant="contained">
             Chiudi
           </Button>
         </DialogActions>
       </Dialog>

      {/* Dialog per checkout pendenti */}
      <Dialog open={checkoutDialogOpen} onClose={() => setCheckoutDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box>
              <Typography variant="h6">Checkout Pendenti</Typography>
              <Typography variant="caption" color="text.secondary">
                Totale: {pendingCheckouts.length} checkout dimenticati
              </Typography>
            </Box>
            {pendingCheckouts.length > 0 && (
              <Button
                variant="contained"
                color="primary"
                startIcon={<CheckIcon />}
                onClick={handleAutoCheckout}
                size="small"
              >
                Checkout Automatico (12h+)
              </Button>
            )}
          </Box>
        </DialogTitle>
        
        <DialogContent>
          {pendingCheckouts.length === 0 ? (
            <Box textAlign="center" py={4}>
              <CheckCircleIcon sx={{ fontSize: 48, color: '#4caf50', mb: 2 }} />
              <Typography variant="h6" color="text.secondary">
                Nessun checkout pendente
              </Typography>
            </Box>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Dipendente</strong></TableCell>
                    <TableCell><strong>Matricola</strong></TableCell>
                    <TableCell><strong>Dipartimento</strong></TableCell>
                    <TableCell><strong>Check-in</strong></TableCell>
                    <TableCell><strong>Ore Totali</strong></TableCell>
                    <TableCell align="right"><strong>Azioni</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pendingCheckouts.map((checkout) => {
                    const checkInTime = new Date(checkout.check_in_time);
                    const hoursSinceCheckIn = (Date.now() - checkInTime.getTime()) / (1000 * 60 * 60);
                    return (
                      <TableRow key={checkout.presence_id} hover>
                        <TableCell>
                          {checkout.nome} {checkout.cognome}
                        </TableCell>
                        <TableCell>{checkout.matricola || 'N/A'}</TableCell>
                        <TableCell>{checkout.dipartimento || 'N/A'}</TableCell>
                        <TableCell>
                          {checkInTime.toLocaleString('it-IT')}
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={`${hoursSinceCheckIn.toFixed(1)}h`}
                            color={hoursSinceCheckIn > 12 ? 'error' : hoursSinceCheckIn > 8 ? 'warning' : 'default'}
                            size="small"
                          />
                        </TableCell>
                        <TableCell align="right">
                          <Button
                            variant="contained"
                            color="primary"
                            size="small"
                            startIcon={<CheckIcon />}
                            onClick={() => handleForceCheckout(checkout.presence_id)}
                          >
                            Forza Checkout
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        
        <DialogActions>
          <Button onClick={() => setCheckoutDialogOpen(false)} variant="contained">
            Chiudi
          </Button>
        </DialogActions>
      </Dialog>

       {/* Dialog per visualizzare l'elenco dipendenti in anomalia */}
       <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>
          {dialogTitle}
          <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>
            Totale: {selectedAnomalies.length} dipendenti
          </Typography>
        </DialogTitle>
        
        <DialogContent>
          <TableContainer component={Paper} sx={{ maxHeight: 600 }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell><strong>Dipendente</strong></TableCell>
                  <TableCell><strong>CF</strong></TableCell>
                  <TableCell><strong>Dipartimento</strong></TableCell>
                  <TableCell align="right"><strong>Ore Previste</strong></TableCell>
                  <TableCell align="right"><strong>Ore Effettive</strong></TableCell>
                  <TableCell align="right"><strong>Differenza</strong></TableCell>
                  <TableCell align="right"><strong>Completamento</strong></TableCell>
                  <TableCell align="center"><strong>Gravità</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {selectedAnomalies.map((emp) => (
                  <TableRow key={emp.employee_id} hover>
                    <TableCell>{emp.nome} {emp.cognome}</TableCell>
                    <TableCell>{emp.codice_fiscale}</TableCell>
                    <TableCell>{emp.dipartimento}</TableCell>
                    <TableCell align="right">{emp.ore_previste}h</TableCell>
                    <TableCell align="right">{emp.ore_effettive}h</TableCell>
                    <TableCell align="right" sx={{ 
                      color: emp.differenza < 0 ? '#f44336' : '#4caf50',
                      fontWeight: 'bold'
                    }}>
                      {emp.differenza > 0 ? '+' : ''}{emp.differenza.toFixed(1)}h
                    </TableCell>
                    <TableCell align="right">
                      <Chip 
                        label={`${emp.percentuale_completamento.toFixed(1)}%`}
                        color={
                          emp.percentuale_completamento < 70 ? 'error' :
                          emp.percentuale_completamento < 85 ? 'warning' : 'success'
                        }
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Chip 
                        label={getSeverityLabel(emp.gravita)}
                        color={getSeverityColor(emp.gravita) as any}
                        size="small"
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} variant="contained">
            Chiudi
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog per visualizzare elenco dipendenti per categoria */}
      <Dialog open={employeesListDialogOpen} onClose={() => setEmployeesListDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {employeesListTitle}
          <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>
            Totale: {employeesList.length} dipendenti
          </Typography>
        </DialogTitle>
        
        <DialogContent>
          {loadingEmployeesList ? (
            <Box display="flex" justifyContent="center" alignItems="center" py={4}>
              <CircularProgress />
            </Box>
          ) : employeesList.length === 0 ? (
            <Box textAlign="center" py={4}>
              <CheckCircleIcon sx={{ fontSize: 48, color: '#4caf50', mb: 2 }} />
              <Typography variant="h6" color="text.secondary">
                Nessun dipendente trovato
              </Typography>
            </Box>
          ) : (
            <TableContainer component={Paper} sx={{ maxHeight: 600 }} variant="outlined">
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Nome</strong></TableCell>
                    <TableCell><strong>Cognome</strong></TableCell>
                    <TableCell><strong>Codice Fiscale</strong></TableCell>
                    <TableCell><strong>Matricola</strong></TableCell>
                    <TableCell><strong>Dipartimento</strong></TableCell>
                    <TableCell><strong>Email</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {employeesList.map((emp) => (
                    <TableRow key={emp.employee_id} hover>
                      <TableCell>{emp.nome}</TableCell>
                      <TableCell>{emp.cognome}</TableCell>
                      <TableCell>{emp.codice_fiscale}</TableCell>
                      <TableCell>{emp.matricola || 'N/A'}</TableCell>
                      <TableCell>{emp.dipartimento || 'N/A'}</TableCell>
                      <TableCell>{emp.email || 'N/A'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        
        <DialogActions>
          <Button onClick={() => setEmployeesListDialogOpen(false)} variant="contained">
            Chiudi
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Dashboard;
